'use client';

import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { completeOAuthRedirectSignIn, formatAuthError, completeInviteJoinLink } from './authActions';
import { inviteJoinAction, strippedInviteHref } from './inviteJoin';
import { signOut } from 'firebase/auth';
import {
  consumeLoginReturnTo,
  persistLoginReturnTo,
  currentPathReturnTo,
  defaultLoginReturnTo,
} from './loginReturnTo';
import { TermsRequiredError } from './establishSession';
import { joinGateFromUnknown } from './joinGate';
import { rememberOpsAuthReturn, returnToSlugAuthOrigin } from '@/platform/lib/idTokenStore';
import {
  isAllowedOpsAuthReturn,
  isCommunioProductSlugHost,
  normalizeOpsAuthReturn,
} from '@/platform/lib/authDomain';
import { getFirebaseAuth } from '@/platform/lib/firebase';
import {
  finishApexHostLoginWithDefaults,
  resolveApexHostLoginDestWithDefaults,
} from '@/platform/saas/apexHostLoginFinish';
import {
  oauthRedirectFailureMessage,
  shouldBounceLoginRoute,
  shouldSkipOAuthRedirectCompletion,
} from './oauthRedirect';

type LoginContextValue = {
  open: boolean;
  returnTo: string;
  /** Prefill for OTP email field (member invite ?email=). */
  inviteEmailHint: string;
  openLogin: (returnTo?: string) => void;
  /** Open the drawer without clearing an OAuth redirect error. */
  revealLogin: (returnTo?: string) => void;
  closeLogin: () => void;
  persistReturnTo: () => void;
  oauthError: string | null;
  clearOauthError: () => void;
};

const LoginContext = createContext<LoginContextValue | null>(null);

function LoginRouteSync({
  oauthSettled,
  hasOAuthCredential,
}: {
  oauthSettled: boolean;
  hasOAuthCredential: boolean;
}) {
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const router = useRouter();
  const { revealLogin } = useLogin();

  useEffect(() => {
    if (
      !shouldBounceLoginRoute({
        pathname,
        oauthSettled,
        hasOAuthCredential,
      })
    ) {
      return;
    }
    const returnTo = searchParams.get('returnTo') || defaultLoginReturnTo();
    const fallback = defaultLoginReturnTo();
    const safeReturn = returnTo.startsWith('/') ? returnTo : fallback;
    const authReturnRaw = searchParams.get('authReturn');
    const authReturn = authReturnRaw ? normalizeOpsAuthReturn(authReturnRaw) : null;
    revealLogin(safeReturn);
    // Slug-originated bounce: stay on /login/?authReturn=… — never soft-nav to apex `/`
    // (that became a dead-end final page when auth failed or the drawer closed).
    if (authReturn) {
      try {
        if (isCommunioProductSlugHost(new URL(authReturn).hostname)) {
          return;
        }
      } catch {
        /* fall through to relative replace */
      }
    }
    router.replace(safeReturn);
  }, [pathname, searchParams, revealLogin, router, oauthSettled, hasOAuthCredential]);

  return null;
}


function InviteQuerySync({
  setInviteEmailHint,
}: {
  setInviteEmailHint: (email: string) => void;
}) {
  const searchParams = useSearchParams();
  const { revealLogin } = useLogin();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    if (typeof window === 'undefined') return;
    const action = inviteJoinAction({
      search: window.location.search,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
    });
    if (action.type === 'none') return;
    ran.current = true;
    if (action.type === 'redirect') {
      window.location.replace(action.href);
      return;
    }
    const revealInviteOtp = (email: string) => {
      setInviteEmailHint(email);
      const current = (getFirebaseAuth().currentUser?.email || '').trim().toLowerCase();
      const invited = email.trim().toLowerCase();
      if (current && current !== invited) {
        void signOut(getFirebaseAuth()).then(() => revealLogin('/'));
        return;
      }
      revealLogin('/');
    };
    if (action.type === 'consume') {
      void (async () => {
        try {
          await completeInviteJoinLink(action.email, action.token);
          window.location.replace(strippedInviteHref(window.location.href));
        } catch {
          revealInviteOtp(action.email);
        }
      })();
      return;
    }
    const current = (getFirebaseAuth().currentUser?.email || '').trim().toLowerCase();
    const invited = action.email.trim().toLowerCase();
    if (current && current !== invited) {
      revealInviteOtp(action.email);
      return;
    }
    if (!current) {
      revealInviteOtp(action.email);
    }
  }, [searchParams, revealLogin, setInviteEmailHint]);

  return null;
}

export function LoginProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const [open, setOpen] = useState(false);
  const [returnTo, setReturnTo] = useState('/');
  const [inviteEmailHint, setInviteEmailHint] = useState('');
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthSettled, setOauthSettled] = useState(shouldSkipOAuthRedirectCompletion);
  const [hasOAuthCredential, setHasOAuthCredential] = useState(false);

  const revealLogin = useCallback((nextReturnTo?: string) => {
    const dest =
      nextReturnTo && nextReturnTo.startsWith('/')
        ? nextReturnTo
        : currentPathReturnTo(
            typeof window !== 'undefined' ? window.location.pathname : '/',
            typeof window !== 'undefined' ? window.location.search : '',
          );
    setReturnTo(dest);
    setOpen(true);
  }, []);

  const openLogin = useCallback(
    (nextReturnTo?: string) => {
      setOauthError(null);
      revealLogin(nextReturnTo);
    },
    [revealLogin],
  );

  const closeLogin = useCallback(() => {
    // Cancel on apex after a slug bounce → return to the originating slug, never stay on apex.
    if (returnToSlugAuthOrigin()) return;
    setOpen(false);
  }, []);

  const persistReturnTo = useCallback(() => {
    persistLoginReturnTo(returnTo);
  }, [returnTo]);

  const clearOauthError = useCallback(() => setOauthError(null), []);

  useEffect(() => {
    if (shouldSkipOAuthRedirectCompletion()) {
      setOauthSettled(true);
      return;
    }
    let alive = true;

    const pendingReturn =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('authReturn')
        : null;
    if (pendingReturn && isAllowedOpsAuthReturn(pendingReturn)) {
      rememberOpsAuthReturn(pendingReturn);
    }

    // Slug return after aborted apex auth: surface the error on the originating page.
    const loginErr =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('loginError')
        : null;
    if (loginErr) {
      setOauthError(loginErr);
      setOpen(true);
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('loginError');
        window.history.replaceState(null, '', `${u.pathname}${u.search}${u.hash}`);
      } catch {
        /* ignore */
      }
    }

    completeOAuthRedirectSignIn()
      .then(async ({ cred, hadPending, currentUser }) => {
        if (cred || currentUser) {
          setHasOAuthCredential(true);
          if (!alive) return;
          try {
            const { dest, bounced } = await finishApexHostLoginWithDefaults(
              consumeLoginReturnTo(defaultLoginReturnTo()),
            );
            if (!alive) return;
            setOpen(false);
            if (!bounced && dest && dest !== '/') {
              routerRef.current.push(dest);
            }
          } catch (err) {
            if (!alive) return;
            if (err instanceof TermsRequiredError) {
              setOpen(false);
              const { dest, bounced } = await resolveApexHostLoginDestWithDefaults(
                consumeLoginReturnTo(defaultLoginReturnTo()),
              );
              if (!bounced && dest && dest !== '/') {
                routerRef.current.push(dest);
              }
              return;
            }
            const gate = joinGateFromUnknown(err);
            if (gate) {
              if (returnToSlugAuthOrigin({ error: gate.message })) return;
              setOauthError(gate.message);
              setOpen(true);
              return;
            }
            throw err;
          }
          return;
        }
        const missing = oauthRedirectFailureMessage(hadPending, cred, currentUser);
        if (missing && alive) {
          if (returnToSlugAuthOrigin({ error: missing })) return;
          setOauthError(missing);
          setOpen(true);
        }
      })
      .catch((err) => {
        if (!alive) return;
        const message = formatAuthError(err);
        if (returnToSlugAuthOrigin({ error: message })) return;
        setOauthError(message);
        setOpen(true);
      })
      .finally(() => {
        if (alive) setOauthSettled(true);
      });

    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      open,
      returnTo,
      inviteEmailHint,
      openLogin,
      revealLogin,
      closeLogin,
      persistReturnTo,
      oauthError,
      clearOauthError,
    }),
    [open, returnTo, inviteEmailHint, openLogin, revealLogin, closeLogin, persistReturnTo, oauthError, clearOauthError],
  );

  return (
    <LoginContext.Provider value={value}>
      <Suspense fallback={null}>
        <LoginRouteSync oauthSettled={oauthSettled} hasOAuthCredential={hasOAuthCredential} />
        <InviteQuerySync setInviteEmailHint={setInviteEmailHint} />
      </Suspense>
      {children}
    </LoginContext.Provider>
  );
}

export function useLogin(): LoginContextValue {
  const ctx = useContext(LoginContext);
  if (!ctx) {
    throw new Error('useLogin must be used within LoginProvider');
  }
  return ctx;
}
